import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, MagnifyingGlass, FileArrowUp, Download, ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Pagination from "../components/Pagination";

export default function Siswa() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [q, setQ] = useState("");
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [filterKelas, setFilterKelas] = useState("all");
  const [filterStatus, setFilterStatus] = useState("aktif");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesBusy, setBatchesBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const fileInput = useRef();
  const canManage = ["superadmin","admin","finance"].includes(user?.role);

  const reqRef = useRef(0);
  const load = async () => {
    const rid = ++reqRef.current;
    const [s, l] = await Promise.all([
      api.get(`/siswa-paged?page=${page}&page_size=${pageSize}` + (filterLembaga !== "all" ? `&lembaga_id=${filterLembaga}` : "") + (q ? `&q=${encodeURIComponent(q)}` : "") + (filterKelas !== "all" ? `&class_name=${encodeURIComponent(filterKelas)}` : "") + (filterStatus !== "all" ? `&status=${filterStatus}` : "")),
      api.get("/lembaga"),
    ]);
    if (rid !== reqRef.current) return; // abaikan respons lama (filter sudah berubah)
    setItems(s.data.items || []);
    setTotal(s.data.total || 0);
    setTotalPages(s.data.total_pages || 1);
    setLembagas(l.data);
  };
  const [classOptions, setClassOptions] = useState([]);
  useEffect(() => {
    api.get("/siswa/classes" + (filterLembaga !== "all" ? `?lembaga_id=${filterLembaga}` : ""))
      .then((r) => setClassOptions(r.data || []))
      .catch(() => setClassOptions([]));
  }, [filterLembaga]);
  useEffect(() => { setPage(1); }, [filterLembaga, filterKelas, filterStatus]);
  useEffect(() => { load(); }, [filterLembaga, filterKelas, filterStatus, page, pageSize]);
  // debounce search
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 400); return () => clearTimeout(t); }, [q]);

  const openNew = () => {
    setEditing(null);
    setForm({ lembaga_id: lembagas[0]?.id || "", nis: "", nisn: "", name: "", gender: "L", birth_place: "", birth_date: "", class_name: "", parent_name: "", parent_phone: "", address: "", status: "aktif" });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/siswa/${editing.id}`, form);
      else await api.post("/siswa", form);
      toast.success(editing ? "Siswa diperbarui" : "Siswa ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus siswa dan semua tagihannya?")) return;
    try { await api.delete(`/siswa/${id}`); toast.success("Terhapus"); load(); } catch (e) { toast.error("Gagal menghapus"); }
  };

  const downloadTemplate = () => {
    // Sheet 1: Template Siswa — pakai KODE LEMBAGA (bukan ObjectId) supaya human-readable
    const templateHeaders = ["lembaga_kode","nis","nisn","name","gender","birth_place","birth_date","class_name","parent_name","parent_phone","address","status"];
    const firstKode = lembagas[0]?.code || lembagas[0]?.name || "";
    const templateSheet = XLSX.utils.aoa_to_sheet([
      templateHeaders,
      [firstKode, "202500001", "0091234567", "Ahmad Fauzi", "L", "Bandung", "2015-05-10", "Kelas 1", "Bapak Fauzi", "081234567890", "Jl. Merdeka No.1", "aktif"],
      [firstKode, "202500002", "0091234568", "Siti Aisyah", "P", "Bandung", "2015-08-15", "Kelas 1", "Ibu Sarah", "081234567891", "Jl. Sudirman No.2", "aktif"],
    ]);
    // Sheet 2: Referensi Kode Lembaga → user copy kode ini ke sheet Template
    const refSheet = XLSX.utils.aoa_to_sheet([
      ["KODE LEMBAGA (copy salah satu ke kolom lembaga_kode di sheet Template)"],
      [],
      ["kode","nama"],
      ...lembagas.map((l) => [l.code || l.name, l.name]),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, templateSheet, "Template");
    XLSX.utils.book_append_sheet(wb, refSheet, "Referensi Lembaga");
    XLSX.writeFile(wb, "template-siswa.xlsx");
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      // Baca sheet Template (kalau tidak ada, pakai sheet pertama)
      const sheetName = wb.SheetNames.includes("Template") ? "Template" : wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(sheet);
      if (!data.length) return toast.error("Excel kosong");
      // Lookup lembaga: kode → id (case insensitive)
      const kodeMap = {};
      lembagas.forEach((l) => {
        if (l.code) kodeMap[l.code.toUpperCase()] = l.id;
        if (l.name) kodeMap[l.name.toUpperCase()] = l.id;
      });
      const errors = [];
      const toStr = (v) => (v === null || v === undefined || v === "") ? null : String(v);
      const validItems = data.filter((d) => d.name && d.nis).map((d, i) => {
        const kode = String(d.lembaga_kode || d.lembaga_id || "").toUpperCase().trim();
        const lid = kodeMap[kode] || (lembagas[0]?.id ?? "");
        if (!kodeMap[kode] && kode) errors.push(`Baris ${i+2}: Kode lembaga "${d.lembaga_kode}" tidak ditemukan, dipakai default`);
        return {
          lembaga_id: lid,
          nis: String(d.nis),
          nisn: toStr(d.nisn),
          name: String(d.name),
          gender: toStr(d.gender),
          birth_place: toStr(d.birth_place),
          birth_date: toStr(d.birth_date),
          class_name: toStr(d.class_name),
          parent_name: toStr(d.parent_name),
          parent_phone: toStr(d.parent_phone),
          address: toStr(d.address),
          status: toStr(d.status) || "aktif",
        };
      });
      const { data: res } = await api.post("/siswa/bulk", validItems);
      toast.success(`${res.inserted} siswa berhasil diimport (batch ${res.import_batch_id})`);
      if (errors.length) alert("Peringatan:\n" + errors.slice(0, 5).join("\n"));
      if (res.errors && res.errors.length) {
        alert(`⚠️ ${res.errors.length} baris ditolak backend:\n` + res.errors.slice(0, 10).map(er => `Baris ${er.row} (NIS ${er.nis}): ${er.error}`).join("\n"));
      }
      load();
    } catch (e) {
      // Tampilkan detail Pydantic 422 kalau ada
      const detail = e.response?.data?.detail;
      let msg = e.message || "unknown";
      if (Array.isArray(detail)) {
        msg = detail.slice(0, 5).map(d => {
          const rowIdx = d.loc?.[1]; // items[N].fieldName → index N
          const field = d.loc?.[2] || d.loc?.slice(-1)[0];
          return `Baris ${typeof rowIdx === "number" ? rowIdx + 2 : "?"} field "${field}": ${d.msg}`;
        }).join("\n");
      } else if (typeof detail === "string") {
        msg = detail;
      }
      alert("Gagal mengimport (422):\n\n" + msg);
      toast.error("Gagal mengimport - cek detail di dialog");
    }
  };

  const openBatches = async () => {
    setBatchesOpen(true);
    setBatchesBusy(true);
    try {
      const { data } = await api.get("/siswa/import-batches");
      setBatches(data.batches || []);
    } catch (e) { toast.error("Gagal ambil batch"); }
    finally { setBatchesBusy(false); }
  };

  const deleteBatch = async (batchId, count) => {
    if (!window.confirm(`Yakin hapus ${count} siswa dari batch ${batchId}?\nSiswa yang sudah punya tagihan/pembayaran akan di-skip.`)) return;
    try {
      const { data } = await api.delete(`/siswa/import-batch/${batchId}`);
      if (data.skipped_has_data) toast.warning(`${data.deleted} dihapus, ${data.skipped_has_data} di-skip (sudah ada tagihan/pembayaran)`);
      else toast.success(`${data.deleted} siswa dihapus`);
      openBatches();
      load();
    } catch (e) { toast.error("Gagal hapus batch"); }
  };

  const filtered = items; // pagination server-side, no client filter
  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l]));
  const displayRows = filtered;

  return (
    <div data-testid="siswa-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Manajemen Siswa</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Data Siswa</h1>
          <p className="text-gray-600 text-sm mt-1">{user?.role === "admin" && user?.lembaga_id ? "Kelola data siswa lembaga Anda." : "Kelola data siswa seluruh lembaga."}</p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="siswa-template-button"><Download size={16} className="mr-1.5" />Template Excel</Button>
            <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} data-testid="siswa-import-file" />
            <Button variant="outline" onClick={() => fileInput.current?.click()} data-testid="siswa-import-button"><FileArrowUp size={16} className="mr-1.5" />Import Excel</Button>
            <Button variant="outline" onClick={openBatches} data-testid="siswa-batches-button" className="border-amber-300 text-amber-700 hover:bg-amber-50"><ClockCounterClockwise size={16} className="mr-1.5" />Riwayat Import</Button>
            <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="siswa-add-button"><Plus size={16} weight="bold" className="mr-1.5" />Tambah Siswa</Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari nama / NIS / NISN..." className="pl-9" data-testid="siswa-search" />
          </div>
          <Select value={filterLembaga} onValueChange={(v)=>{setFilterLembaga(v); setFilterKelas("all");}}>
            <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="siswa-lembaga-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Lembaga</SelectItem>
              {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterKelas} onValueChange={setFilterKelas}>
            <SelectTrigger className="w-full sm:w-40 bg-white" data-testid="siswa-kelas-filter"><SelectValue placeholder="Semua Kelas" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="all">Semua Kelas</SelectItem>
              {classOptions.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-36 bg-white" data-testid="siswa-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="lulus">Lulus</SelectItem>
              <SelectItem value="pindah">Pindah</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-gray-500 font-mono">Hal {page}/{totalPages} · {total} total</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">NIS</th>
                <th className="px-4 py-3 font-semibold">Nama</th>
                <th className="px-4 py-3 font-semibold">L/P</th>
                <th className="px-4 py-3 font-semibold">Kelas</th>
                <th className="px-4 py-3 font-semibold">Lembaga</th>
                <th className="px-4 py-3 font-semibold">Orang Tua</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((s) => (
                <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{s.nis}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">{s.name}</td>
                  <td className="px-4 py-2.5">{s.gender || "-"}</td>
                  <td className="px-4 py-2.5">{s.class_name || "-"}</td>
                  <td className="px-4 py-2.5 text-xs">{lembagaMap[s.lembaga_id]?.name || "-"}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs">{s.parent_name || "-"}</div>
                    <div className="text-xs text-gray-500 font-mono">{s.parent_phone || ""}</div>
                  </td>
                  <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${s.status==="aktif"?"bg-[#CCFBF1] text-[#0B8D95]":"bg-gray-100 text-gray-600"}`}>{s.status}</span></td>
                  <td className="px-4 py-2.5 text-right">
                    {canManage && <>
                      <button onClick={()=>openEdit(s)} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" data-testid={`siswa-edit-${s.id}`}><PencilSimple size={14} /></button>
                      <button onClick={()=>remove(s.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`siswa-delete-${s.id}`}><Trash size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {displayRows.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">Belum ada siswa</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setPage} testIdPrefix="siswa" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-2xl" data-testid="siswa-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Siswa" : "Tambah Siswa"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Lembaga *</Label>
              <Select value={form.lembaga_id} onValueChange={(v)=>setForm({...form, lembaga_id: v})}>
                <SelectTrigger className="bg-white" data-testid="siswa-lembaga-select"><SelectValue placeholder="Pilih lembaga" /></SelectTrigger>
                <SelectContent className="bg-white">{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>NIS *</Label><Input value={form.nis || ""} onChange={(e)=>setForm({...form, nis: e.target.value})} data-testid="siswa-nis-input" /></div>
            <div><Label>NISN</Label><Input value={form.nisn || ""} onChange={(e)=>setForm({...form, nisn: e.target.value})} data-testid="siswa-nisn-input" placeholder="10 digit Nomor Induk Siswa Nasional" /></div>
            <div className="col-span-2"><Label>Nama Lengkap *</Label><Input value={form.name || ""} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="siswa-name-input" /></div>
            <div><Label>Jenis Kelamin</Label>
              <Select value={form.gender || "L"} onValueChange={(v)=>setForm({...form, gender: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Kelas</Label><Input value={form.class_name || ""} onChange={(e)=>setForm({...form, class_name: e.target.value})} /></div>
            <div><Label>Tempat Lahir</Label><Input value={form.birth_place || ""} onChange={(e)=>setForm({...form, birth_place: e.target.value})} data-testid="siswa-birthplace-input" /></div>
            <div><Label>Tanggal Lahir</Label><Input type="date" value={form.birth_date || ""} onChange={(e)=>setForm({...form, birth_date: e.target.value})} /></div>
            <div><Label>Status</Label>
              <Select value={form.status || "aktif"} onValueChange={(v)=>setForm({...form, status: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="lulus">Lulus</SelectItem><SelectItem value="pindah">Pindah</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Nama Orang Tua / Wali</Label><Input value={form.parent_name || ""} onChange={(e)=>setForm({...form, parent_name: e.target.value})} data-testid="siswa-parent-name" /></div>
            <div><Label>No. HP Orang Tua / Wali</Label><Input value={form.parent_phone || ""} onChange={(e)=>setForm({...form, parent_phone: e.target.value})} placeholder="Contoh: 081234567890" data-testid="siswa-parent-phone" /></div>
            <div className="col-span-2"><Label>Alamat</Label><Input value={form.address || ""} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="siswa-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
        <DialogContent className="bg-white max-w-4xl" data-testid="siswa-batches-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={20} className="text-amber-700" />Riwayat Import Siswa</DialogTitle></DialogHeader>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 mb-3">
            Klik 🗑 untuk hapus semua siswa dari 1 batch. <b>Siswa yang sudah punya tagihan/pembayaran akan di-skip</b> otomatis (mencegah data yatim).
          </div>
          {batchesBusy ? <div className="p-6 text-center text-muted-foreground">Memuat...</div> : (
            <div className="max-h-96 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr className="text-left">
                  <th className="px-3 py-2 font-bold">Waktu Import</th>
                  <th className="px-3 py-2 font-bold">Lembaga</th>
                  <th className="px-3 py-2 font-bold">Kelas</th>
                  <th className="px-3 py-2 font-bold text-right">Jml Siswa</th>
                  <th className="px-3 py-2 font-bold text-center">Aksi</th>
                </tr></thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Belum ada batch import</td></tr>}
                  {batches.map((b) => (
                    <tr key={b.batch_id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{(b.first_created || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 font-semibold text-xs">{(b.lembaga_names || []).join(", ") || "-"}</td>
                      <td className="px-3 py-2 text-xs">{(b.classes || []).join(", ") || "-"}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.count}</td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="outline" onClick={() => deleteBatch(b.batch_id, b.count)} data-testid={`siswa-batch-delete-${b.batch_id}`} className="text-rose-600 border-rose-300 hover:bg-rose-50"><Trash size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setBatchesOpen(false)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
